<template>
  <div
    :class="{
      funnel: !analyticsImpressionLoading && !landingpagesLoading && !isNewUser,
      'funnel-new-user': !analyticsImpressionLoading && !landingpagesLoading && isNewUser,
    }"
  >
    <v-skeleton-loader
      v-if="analyticsImpressionLoading || landingpagesLoading"
      :style="{ width: $vuetify.display.mdAndUp ? '92%' : '100%' }"
      class="mx-auto"
      type="article, actions"
    />

    <v-card
      v-else
      :class="{ 'card-new-user': isNewUser }"
      class="pa-3 pb-0 card"
    >
      <v-card-title
        id="impressions-title"
        class="mb-2 pa-0 title d-flex justify-space-between"
      >
        <template v-if="isNewUser">
          <span>{{ $t('dashboard.impressions.new-user-title') }}</span>
        </template>
        <template v-else>
          <span>{{ $t('dashboard.impressions.title') }}</span>
        </template>

        <span
          v-if="!isNewUser && !isLightUser"
          class="mt-2 mr-2 details-label"
          @click="emit('open-details')"
        >
          {{ $t('dashboard.details.icon-label') }}
        </span>
      </v-card-title>
      <BlurredBackdropUpselling v-if="needsUpgrade" />
      <template v-else>
        <Feature :feature-slug="featureNames.LANDINGPAGE">
          <v-card-text
            v-if="isNewUser"
            class="pa-0"
          >
            <div class="d-md-flex justify-md-space-between mb-4">
              <span class="label d-flex d-md-block my-2 my-md-0 mr-1">
                {{
                  $t('dashboard.impressions.new-user-labels.0', {
                    count: numberOfLandingpages?.toLocaleString(),
                  })
                }}
              </span>
              <v-btn
                v-if="!isMasterView"
                @click="integrateWidgets()"
                color="white"
              >
                <span class="button text-primary">
                  {{ $t('buttons.dashboard.impressions.new-user-labels.0') }}
                </span>
              </v-btn>
            </div>
          </v-card-text>

          <v-card-text
            v-else
            class="pa-0"
          >
            <div class="d-flex justify-space-between flex-wrap mb-4">
              <div>
                <span class="d-flex">
                  <span class="d-flex align-center">
                    <v-avatar
                      color="rgba(255, 255, 255, 0.2)"
                      size="36"
                    >
                      <v-icon
                        icon="mdi-eye"
                        color="white"
                      />
                    </v-avatar>
                  </span>
                  <div class="d-flex flex-column ml-md-2 ml-1">
                    <span class="label">{{
                      $t('dashboard.impressions.existing-user-labels.0')
                    }}</span>
                    <span class="number">{{
                      analyticsImpression?.overallTotal?.toLocaleString() || 0
                    }}</span>
                  </div>
                </span>
              </div>
              <div>
                <div class="d-flex">
                  <span class="d-flex align-center">
                    <v-avatar
                      color="rgba(255, 255, 255, 0.2)"
                      size="36"
                    >
                      <v-icon
                        icon="mdi-button-pointer"
                        color="white"
                      />
                    </v-avatar>
                  </span>
                  <div class="d-flex flex-column ml-md-2 ml-1">
                    <span class="label">{{
                      $t('dashboard.impressions.existing-user-labels.1')
                    }}</span>
                    <span class="number">{{ numberOfLandingpages?.toLocaleString() || 0 }} </span>
                  </div>
                </div>
              </div>
              <div
                class="mt-4 mt-xl-0"
                :style="{ width: $vuetify.display.lgAndDown ? '100%' : 'auto' }"
                v-if="!isExpertUser($auth.user) && !isPlatformSmg"
              >
                <v-btn
                  v-if="!isMasterView"
                  @click="getMore()"
                  style="width: 100%"
                  color="white"
                >
                  <span class="button text-primary">
                    {{ $t('buttons.dashboard.impressions.existing-user-labels.0') }}
                  </span>
                </v-btn>
              </div>
            </div>
          </v-card-text>
        </Feature>
      </template>
    </v-card>
  </div>
</template>
<script setup lang="ts">
import { useQuery } from '@vue/apollo-composable'
import { computed, defineEmits, getCurrentInstance, inject, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import BlurredBackdropUpselling from '@/components/BlurredBackdropUpselling.vue'
import featureNames from '@/lib/featureNames'
import { isExpertUser } from '@/lib/productTypes'
import brandingMixin from '@/mixins/branding'
import featureMixin from '@/mixins/feature'
import GET_ANALYTICS_IMPRESSIONS from '@/modules/dashboard/GetAnalyticsImpressionForCompany'
import GET_NUMBER_OF_LANDINGPAGES from '@/modules/dashboard/GetNumberOfLandingpages.gql'
import { Feature } from '@/plugins/feature-config'

const router = useRouter()
const route = useRoute()
const { appContext } = getCurrentInstance()!
const $tracking = appContext.config.globalProperties.$tracking
const proxy = getCurrentInstance()?.proxy
const getFeature = featureMixin.methods.getFeature.bind(proxy)
const $auth = inject('auth')
const routeParamId = computed(() => route?.params?.id || null)

defineOptions({
  mixins: [brandingMixin],
})

defineProps({
  isNewUser: Boolean,
  isLightUser: Boolean,
})

const isPlatformSmg = computed(() => proxy?.isPlatformSmg)
const isMasterView = computed(() => route.path.startsWith('/master'))
const emit = defineEmits([
  'open-details',
  'update-is-new-user',
  'analytics-impressions-loading-update',
])

const {
  result: landingpages,
  loading: landingpagesLoading,
  refetch: refetchLandingpages,
} = useQuery(GET_NUMBER_OF_LANDINGPAGES, {
  isMasterView: route.path.startsWith('/master'),
  childCompanyId: routeParamId.value,
})

const numberOfLandingpages = computed(() => landingpages.value?.numberOfLandingpages)

const {
  result: analytics,
  loading: analyticsImpressionLoading,
  refetch: refetchAnalytics,
} = useQuery(GET_ANALYTICS_IMPRESSIONS, {
  isMasterView: isMasterView.value,
  childCompanyId: routeParamId.value,
})

const analyticsImpression = computed(() => analytics.value?.analyticsImpression)

const landingpageFeature = computed(() => getFeature(featureNames.LANDINGPAGE))
const needsUpgrade = ref(!landingpageFeature.value.isActive)

const getMore = async () => {
  $tracking.event('Dashboard', 'Clicked', 'Landingpages')
  needsUpgrade.value = true
}
const integrateWidgets = async () => {
  $tracking.event('Dashboard', 'Clicked', 'Landingpages Integrate Widgets')
  router.push({ name: 'landingpages', params: { slug: 'guide' } })
}

watch(
  analyticsImpression,
  (newValue) => {
    emit('update-is-new-user', { analyticsImpression: newValue?.overallTotal ?? 0 })
  },
  { immediate: true }
)

watch(
  () => route.params.id,
  async (newId, oldId) => {
    if (newId !== oldId) {
      await refetchAnalytics({ childCompanyId: newId })
      await refetchLandingpages({ childCompanyId: newId })
    }
  }
)

watch(
  analyticsImpressionLoading,
  (newValue) => {
    emit('analytics-impressions-loading-update', newValue)
  },
  { immediate: true }
)
</script>

<style scoped>
.details-label {
  cursor: pointer;
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  line-height: 16px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  text-decoration: underline;
}
:deep(.blur-layer) {
  background: transparent;
}
.grid {
  display: grid;
}

.funnel::after {
  content: '';
  position: absolute;
  top: 99%;
  left: 30px;
  right: 30px;
  border-top: solid 20px #279491;
  border-left: solid 50px transparent;
  border-right: solid 50px transparent;
}
.card {
  width: 92%;
  margin: 0 auto;
  background: #229e9a;
}

.funnel-new-user::after {
  content: '';
  position: absolute;
  top: 99%;
  left: 43px;
  right: 43px;
  border-top: solid 20px #24a9a5;
  border-left: solid 50px transparent;
  border-right: solid 50px transparent;
}

.card-new-user {
  width: 87%;
  background: #26b1ad;
}

@media (max-width: 960px) {
  .v-card {
    margin: 0;
    width: 100%;
  }
}

@media (max-width: 600px) {
  :deep(.centered-alert) {
    width: 100% !important;
  }
}

@media (max-width: 350px) {
  .details-label {
    font-size: 10px;
  }
}
</style>
